import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Users, Scale } from "lucide-react";
import { fmtUSD, FLAGS } from "@/lib/utils";
import { useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";
import DevStatusBadge from "@/components/DevStatusBadge";
import { SecureImage } from "@/components/ImageUploader";
import VerifiedBadges from "@/components/VerifiedBadges";

export default function PropertyCard({ dev }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const inComparison = (user?.comparisons || []).includes(dev.id);
  const atCap = (user?.comparisons || []).length >= 2 && !inComparison;
  async function toggleCompare(e) {
    e.preventDefault(); e.stopPropagation();
    if (atCap) return;
    try {
      await api.post(`/comparisons/toggle/${dev.id}`);
      await refreshUser();
    } catch { /* ignore */ }
  }
  return (
    <div
      onClick={() => navigate(`/development/${dev.id}`)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") navigate(`/development/${dev.id}`); }}
      data-testid={`property-card-${dev.id}`}
      className="group block bg-white rounded-3xl border border-neutral-200/70 overflow-hidden hover:shadow-xl hover:shadow-black/5 hover:-translate-y-1 transition-all duration-300 relative cursor-pointer"
    >
      {user?.role === "investor" && (
        <button
          type="button"
          data-testid={`compare-toggle-${dev.id}`}
          onClick={toggleCompare}
          disabled={atCap}
          title={atCap ? "Comparison full (max 2)" : inComparison ? "Remove from comparison" : "Add to compare"}
          className={`absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center transition backdrop-blur-md ${inComparison ? "bg-propera-gold text-neutral-900" : atCap ? "bg-white/70 text-neutral-300 cursor-not-allowed" : "bg-white/90 text-neutral-600 hover:bg-white"}`}
          aria-label="Toggle comparison"
        >
          <Scale size={15} />
        </button>
      )}
      <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
        <SecureImage
          src={dev.cover_image}
          alt={dev.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-4 left-4 right-14 flex items-center gap-1.5 flex-wrap">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 backdrop-blur-md text-neutral-800">
            {FLAGS[dev.country]} {dev.country}
          </span>
          <VerifiedBadges dev={dev} variant="overlay" compact />
        </div>
        <div className="absolute bottom-4 left-4 flex items-center gap-2">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-black/70 backdrop-blur-md text-white">
            {dev.available_units}/{dev.total_units} units available
          </span>
          {dev.status && <DevStatusBadge status={dev.status} />}
        </div>
      </div>

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="text-lg font-bold text-neutral-900 leading-tight">{dev.name}</h3>
        </div>
        <p className="text-sm text-neutral-500 flex items-center gap-1">
          <MapPin size={13} /> {dev.city}, {dev.country}
        </p>
        <p className="text-xs text-neutral-400 mt-2">
          by{" "}
          {dev.developer_id ? (
            <Link
              to={`/u/${dev.developer_id}`}
              data-testid={`developer-link-${dev.developer_id}`}
              onClick={(e) => e.stopPropagation()}
              className="text-propera font-medium hover:underline"
            >
              {dev.developer_name}
            </Link>
          ) : (
            <span className="text-propera font-medium">{dev.developer_name}</span>
          )}
        </p>

        <div className="flex flex-wrap gap-1.5 mt-3">
          {(dev.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-neutral-100 text-neutral-700">
              {t}
            </span>
          ))}
        </div>

        {dev.payment_plan && (
          <div className="flex items-center gap-1 mt-4 text-[11px] text-neutral-500">
            <span className="font-semibold">Plan:</span>
            <span>{dev.payment_plan.booking}% / {dev.payment_plan.construction}% / {dev.payment_plan.handover}%</span>
          </div>
        )}

        <div className="flex items-end justify-between mt-4 pt-4 border-t border-neutral-100">
          <div>
            <p className="text-xs text-neutral-500">Starting from</p>
            <p className="text-xl font-bold text-neutral-900 tabular">{fmtUSD(dev.starting_price)}</p>
          </div>
          <span className="text-xs font-semibold text-propera flex items-center gap-1">
            <Users size={12} /> {dev.interest_count} interested
          </span>
        </div>
      </div>
    </div>
  );
}
